import { format } from 'date-fns';
import { simpleDateToDate } from './dates';
import type { SimpleDate } from './types';

/*
  Every way a finished puzzle gets written down: on the solved screen and in a shared result.

  They live together because the two voices have to stay recognisably related — the screen says
  "33 sec 🎯 Perfect game" and the share says "0:33 🎯 a perfect game" — and shared across clients
  because a player who finishes on the app and shares from the website should recognise both.
*/

/** Clock form, for shares: "0:33", "12:05", "1:02:30". */
export function formatTimeCompact(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');

  return hrs > 0 ? `${hrs}:${pad(mins)}:${pad(secs)}` : `${mins}:${pad(secs)}`;
}

/** Spoken form, for the screen: "33 sec", "2 min, 5 sec". */
export function formatTimeVerboseShort(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) return `${hrs} hr, ${mins} min, ${secs} sec`;
  if (mins > 0) return `${mins} min, ${secs} sec`;
  return `${secs} sec`;
}

/** On-screen result: the assists alone, or prefixed with the time. */
export function formatResultText(assists: number, seconds?: number): string {
  const assistsText = assists > 0 ? `💡${assists} Hints and checks` : `🎯 Perfect game`;
  return seconds !== undefined ? `${formatTimeCompact(seconds)}  ${assistsText}` : assistsText;
}

/** The same fact in the share's lower-case voice: "🎯 a perfect game", "💡 2 hints/checks". */
export function formatShareAssists(assists: number): string {
  return assists > 0 ? `💡 ${assists} hints/checks` : '🎯 a perfect game';
}

export function formatBoardDate(boardDate: SimpleDate): string {
  return format(simpleDateToDate(boardDate), 'EEEE, MMMM d').trim();
}

/** What one finished puzzle looks like when a player passes it on. */
export interface ShareableResult {
  gameLabel: string;
  gameEmoji: string;
  durationSec: number;
  assists: number;
  boardDate: SimpleDate;
  /** Practice and tutorial boards name themselves; a daily is named by its date. */
  boardName?: string;
}

/** The share's last line, the result itself: "⭐ 0:33 🎯 a perfect game". */
export function formatShareResult({
  gameEmoji,
  durationSec,
  assists,
}: Pick<ShareableResult, 'gameEmoji' | 'durationSec' | 'assists'>): string {
  return `${gameEmoji} ${formatTimeCompact(durationSec)} ${formatShareAssists(assists)}`;
}

/**
 * The shared block, identical on every client — three lines, the last one the result itself.
 *
 * Whether a link goes with it is the sharer's business and each platform's: the website appends the
 * page URL when the player's settings allow, and a native share sheet attaches its own.
 */
export function formatShareText({
  gameLabel,
  gameEmoji,
  durationSec,
  assists,
  boardDate,
  boardName,
}: ShareableResult): string {
  const puzzleLabel = boardName ?? formatBoardDate(boardDate);
  return `Inkwell Games - ${gameLabel}\n${puzzleLabel}\n${formatShareResult({ gameEmoji, durationSec, assists })}`;
}

/**
 * When an earlier attempt was finished, as the history rows date it: "Aug 27, 3:15pm".
 *
 * `completedAt` arrives as a Date from the store and as a string once a play has been through the
 * server, so both are read. An unparseable one is left blank rather than printed as "Invalid Date" —
 * the row's result still says something worth reading.
 */
export function formatCompletedAt(completedAt: Date | string): string {
  const when = completedAt instanceof Date ? completedAt : new Date(completedAt);
  return Number.isNaN(when.getTime()) ? '' : format(when, 'MMM d, h:mmaaa');
}

/** What a non-subscriber is offered in place of their earlier attempts. */
export const RESULTS_PROMO_CTA = 'Subscribe to see all your results';

/*
  What the solved screen shouts. One is picked at random per solve, which is most of the screen's
  character — the same word every day would read as a template.
*/
export const CONGRATS_WORDS = [
  'Brilliant!',
  'Fantastic!',
  'Marvelous!',
  'Bravo!',
  'Splendid!',
  'Amazing!',
  'You did it!',
  'Beautiful!',
  'Delightful!',
  'Magnificent!',
  'Look at that!',
  'Yippee!',
  'Huzzah!',
  'Unbelievable!',
  'How lovely!',
  'Spectacular!',
  'Fabulous!',
  'Tremendous!',
  'Stellar!',
  'Stupendous!',
  'Remarkable!',
  'Incredible!',
  'Wondrous!',
  'Smashing!',
  'Ta-da!',
  'Voilà!',
  'Nicely done!',
  'Hooray!',
  'Aha!',
  'Eureka!',
  'Bingo!',
  'What a gem!',
  'Golly!',
  'My word!',
  'How grand!',
  'Goodness!',
] as const;

/** One of them, at random. */
export const randomCongratsWord = (): string => CONGRATS_WORDS[Math.floor(Math.random() * CONGRATS_WORDS.length)];
